import { useState } from 'react';
import { createPortal } from 'react-dom';

type GenerationMode = 'sequential_context' | 'parallel';
type RegenerateScope = 'all' | 'current';

type CarouselRegenerateModalProps = {
  defaultMode: GenerationMode;
  totalSlides: number;
  currentSlideIndex: number;
  onClose: () => void;
  onConfirm: (_args: { scope: RegenerateScope; mode: GenerationMode }) => void;
};

export function CarouselRegenerateModal({
  defaultMode,
  totalSlides,
  currentSlideIndex,
  onClose,
  onConfirm,
}: CarouselRegenerateModalProps) {
  const [scope, setScope] = useState<RegenerateScope>('all');
  const [mode, setMode] = useState<GenerationMode>(defaultMode);

  if (typeof document === 'undefined') {
    return null;
  }

  const optionRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    padding: '8px 4px',
    cursor: 'pointer',
  };
  const subTextStyle: React.CSSProperties = {
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-sm)',
    marginTop: 2,
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h2>Re-render carousel images</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body" style={{ display: 'grid', gap: 16 }}>
          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend style={{ fontWeight: 700, marginBottom: 6 }}>What to re-render</legend>

            <label style={optionRowStyle}>
              <input
                type="radio"
                name="cr-scope"
                checked={scope === 'all'}
                onChange={() => setScope('all')}
                style={{ marginTop: 4 }}
              />
              <span>
                <div style={{ fontWeight: 600 }}>All {totalSlides} slides</div>
                <div style={subTextStyle}>Re-renders every image in this carousel.</div>
              </span>
            </label>

            <label style={optionRowStyle}>
              <input
                type="radio"
                name="cr-scope"
                checked={scope === 'current'}
                onChange={() => setScope('current')}
                style={{ marginTop: 4 }}
              />
              <span>
                <div style={{ fontWeight: 600 }}>Just slide {currentSlideIndex + 1}</div>
                <div style={subTextStyle}>
                  Re-renders only the currently selected slide. Other slides aren&apos;t touched.
                </div>
              </span>
            </label>
          </fieldset>

          {scope === 'all' && (
            <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
              <legend style={{ fontWeight: 700, marginBottom: 6 }}>How</legend>

              <label style={optionRowStyle}>
                <input
                  type="radio"
                  name="cr-mode"
                  checked={mode === 'sequential_context'}
                  onChange={() => setMode('sequential_context')}
                  style={{ marginTop: 4 }}
                />
                <span>
                  <div style={{ fontWeight: 600 }}>Sequential</div>
                  <div style={subTextStyle}>
                    Style consistency — keeps a coherent look across slides.
                  </div>
                </span>
              </label>

              <label style={optionRowStyle}>
                <input
                  type="radio"
                  name="cr-mode"
                  checked={mode === 'parallel'}
                  onChange={() => setMode('parallel')}
                  style={{ marginTop: 4 }}
                />
                <span>
                  <div style={{ fontWeight: 600 }}>Parallel (Faster)</div>
                  <div style={subTextStyle}>Speed first — renders all slides at once.</div>
                </span>
              </label>
            </fieldset>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-success" onClick={() => onConfirm({ scope, mode })}>
            {scope === 'all'
              ? `Re-render all ${totalSlides}`
              : `Re-render slide ${currentSlideIndex + 1}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
